import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatGNF } from "@/constants/mcevents";
import { CalendarDays, MapPin, Ticket, Search, Loader2 } from "lucide-react";
import { imgSrc } from "@/lib/utils";
import { useContent } from "@/contexts/ContentContext";

const HERO_FALLBACK = "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=800&q=80";

export default function Events() {
  const { t } = useContent();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (city) params.set("city", city);
      const { data } = await api.get(`/public/events?${params}`);
      setEvents(data.events || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="events-page">
      <section className="border-b border-border kente-bg">
        <div className="container-mc py-12 lg:py-16 relative">
          <p className="overline mb-3">{t("events.overline", "🎟️ Billetterie MCZ EVENTS")}</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            {t("events.title_line1", "Vibrez au rythme")}<br/>
            <span className="text-[hsl(var(--primary))]">{t("events.title_line2", "des concerts guinéens")}</span>.
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-xl">
            {t("events.subtitle", "Réservez vos places pour les prochains événements. Billet numérique avec QR code, valable directement sur votre téléphone.")}
          </p>
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input data-testid="events-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Titre, artiste…" className="input-mc pl-9 w-full" />
            </div>
            <input data-testid="events-city-input" value={city} onChange={(e) => setCity(e.target.value)}
                   placeholder="Ville" className="input-mc sm:w-48" />
            <button data-testid="events-search-btn" className="btn-primary">Rechercher</button>
          </form>
        </div>
      </section>

      <section className="container-mc py-14">
        {loading ? (
          <div className="py-24 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /></div>
        ) : events.length === 0 ? (
          <div className="card-mc p-16 text-center" data-testid="no-events">
            <Ticket className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
            <h2 className="font-display font-black text-2xl tracking-tighter">{t("events.empty_title", "Aucun événement à l'affiche")}</h2>
            <p className="text-sm text-muted-foreground mt-2">{t("events.empty_desc", "Revenez bientôt — les artistes publient régulièrement de nouveaux concerts.")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((ev) => <EventCard key={ev.id} event={ev} />)}
          </div>
        )}
      </section>
    </div>
  );
}

function EventCard({ event }) {
  const dateStr = event.event_date
    ? new Date(event.event_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })
    : "Date à venir";
  const soldOut = event.available === 0;
  return (
    <Link to={`/events/${event.id}`} data-testid={`event-card-${event.id}`}
          className="card-mc overflow-hidden group hover:border-foreground transition-colors block">
      <div className="aspect-[16/10] bg-muted relative overflow-hidden">
        <img src={imgSrc(event.cover_image) || HERO_FALLBACK} alt={event.title}
             className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-3 left-3 gn-strip h-1 w-14" />
        {soldOut && <span className="absolute top-3 right-3 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-destructive text-destructive-foreground">Complet</span>}
      </div>
      <div className="p-5">
        <p className="overline mb-1.5">{event.artist_name}</p>
        <h3 className="font-display font-black text-xl tracking-tighter leading-tight line-clamp-2">{event.title}</h3>
        <div className="mt-3 flex flex-col gap-1.5 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" /> {dateStr}</span>
          <span className="inline-flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {event.venue} · {event.city}</span>
        </div>
        <div className="mt-4 flex items-baseline justify-between border-t border-border pt-3">
          <span className="text-[10px] font-mono uppercase text-muted-foreground">À partir de</span>
          <span className="font-display font-black text-lg tracking-tighter">{formatGNF(event.min_price_gnf)}</span>
        </div>
      </div>
    </Link>
  );
}
